import { AccountingTypeLabelModifier } from '@/features/master-table/models';
import { AdministrativeReformTeamEvaluationOptionLabelModifier } from '@/features/project/_models/project-filter';
import { CandidateOfInspectionByExternalExpertLabelModifier } from '@/features/search/_models/projects-search';
import {
  IndicatorDataNatureTypeModifier,
  IndicatorTermModifier,
  IndicatorTypeModifier,
} from '@/features/search/_utils/enum';
import {
  getAmountSearchTagLabel,
  getSearchKeyword,
} from '@/features/search/_utils/projects-search';
import { t } from '@/i18n';
import { ITEM_SPLITTER_COMMA, logger } from '@/utils';
import {
  FundSheetTypeLabelModifier,
  ImplementationMethodLabelModifier,
  ProjectCategoryLabelModifier,
  ReviewTypeLabelModifier,
  SheetTypeLabelModifier,
} from '@/utils/enum';
import type { ProjectsPageSearchQueryStates } from '../../_hooks/search-projects/search-query-states';
import { PublishedLabelModifier } from '../_models/project-filter';

type ReturnType = { label: string; value: string | number };

type SearchQueryStateKeys = keyof ProjectsPageSearchQueryStates;
type ValueType<K extends SearchQueryStateKeys> =
  ProjectsPageSearchQueryStates[K];

const getNonNullableValue = <K extends SearchQueryStateKeys>(
  value: any,
): NonNullable<ValueType<K>> => {
  return value as NonNullable<ValueType<K>>;
};

const FILTER_ORDER = [
  'fiscalYear',
  'sheetType',
  'fundSheetType',
  'q',
  'organizationIds',
  'corporateNumber',
  'categories',
  'implementationMethods',
  'policyIds',
  'accountingType',
  'accountingTypeIds',
  'skipAdministrativeReformTeamEvaluation',
  'isPublished',
  'startYear',
  'endYear',
  'otherResponsibleOrganizationIds',
  'expenditureBudgetPurposeIds',
  'lastImplementedFiscalYearRangeMin',
  'lastImplementedFiscalYearRangeMax',
  'inspectionByExternalExpertReviewType',
  'isCandidateOfInspectionByExternalExpert',
  'formulaConditions',
  'projectNumbers',
  'indicatorKeyword',
  'indicatorKeywordSearchOnlyTitleAndDescription',
  'indicatorType',
  'hasOnlyQualitativeIndicator',
  'indicatorTerm',
  'indicatorDataNatureType',
] satisfies SearchQueryStateKeys[];

export const generateSearchHistoryLabelsMap = (
  filterCondition: ProjectsPageSearchQueryStates,
  inputCascaderAndAccountingLabels: {
    nestedOrganizationIds: string[];
    nestedOtherResponsibleOrganizationIds: string[];
    nestedPolicyIds: string[];
    nestedExpenditureBudgetPurposeIds: string[];
    accountingTypeIds: string[];
  },
  corporateName: string | null,
): ReturnType[] => {
  return FILTER_ORDER.flatMap<ReturnType>((key) => {
    const _value = filterCondition[key as SearchQueryStateKeys];
    const rsOnly = filterCondition.sheetType === 'RS';

    const cascaderKeys = [
      'organizationIds',
      'otherResponsibleOrganizationIds',
      'policyIds',
      'expenditureBudgetPurposeIds',
    ] as SearchQueryStateKeys[];

    if (
      (!_value && !cascaderKeys.includes(key)) ||
      (!rsOnly &&
        (key === 'policyIds' || key === 'expenditureBudgetPurposeIds'))
    ) {
      return [];
    }

    switch (key) {
      case 'fiscalYear': {
        const value = getNonNullableValue<typeof key>(_value);
        return [{ label: t('created-fiscal-year'), value }];
      }
      case 'sheetType': {
        const value = getNonNullableValue<typeof key>(_value);
        return [
          {
            label: t('sheet-type'),
            value: SheetTypeLabelModifier[value],
          },
        ];
      }
      case 'isPublished': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('publish-ability'),
          value: PublishedLabelModifier[value],
        };
      }
      case 'categories': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('project-category'),
          value: value
            .map((item) => ProjectCategoryLabelModifier[item])
            .join(ITEM_SPLITTER_COMMA),
        };
      }
      case 'fundSheetType': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('fund-sheet-type'),
          value: FundSheetTypeLabelModifier[value],
        };
      }
      case 'startYear': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('start-fiscal-year'),
          value,
        };
      }
      case 'endYear': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('end-fiscal-year'),
          value,
        };
      }
      case 'accountingType': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('accounting-type'),
          value: AccountingTypeLabelModifier[value],
        };
      }
      case 'accountingTypeIds': {
        return {
          label: t('accounting-name'),
          value:
            inputCascaderAndAccountingLabels.accountingTypeIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'implementationMethods': {
        const implementationMethods = getNonNullableValue<typeof key>(_value);
        return {
          label: t('implementation-method'),
          value: implementationMethods
            .map((item) => ImplementationMethodLabelModifier[item])
            .join(ITEM_SPLITTER_COMMA),
        };
      }
      case 'skipAdministrativeReformTeamEvaluation': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('filter-by-skip-administrative-reform-team-evaluation'),
          value: AdministrativeReformTeamEvaluationOptionLabelModifier[value],
        };
      }
      case 'projectNumbers': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('project-number'),
          value,
        };
      }
      case 'inspectionByExternalExpertReviewType': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('inspection-target'),
          value: value
            .map((item) => ReviewTypeLabelModifier[item])
            .join(ITEM_SPLITTER_COMMA),
        };
      }
      case 'isCandidateOfInspectionByExternalExpert': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('candidate-of-inspection-by-external-expert'),
          value: CandidateOfInspectionByExternalExpertLabelModifier[value],
        };
      }
      case 'corporateNumber': {
        if (!corporateName) {
          return [];
        }
        return {
          label: t('payment-name'),
          value: corporateName,
        };
      }
      case 'q': {
        const q = getNonNullableValue<typeof key>(_value);
        return {
          label: t('search-keyword'),
          value:
            getSearchKeyword(q, filterCondition.isSearchTargetProjectName) ?? q,
        };
      }
      case 'expenditureBudgetPurposeIds': {
        return {
          label: t('expenditure-budget-purpose'),
          value:
            inputCascaderAndAccountingLabels.nestedExpenditureBudgetPurposeIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'policyIds': {
        return {
          label: t('policy'),
          value:
            inputCascaderAndAccountingLabels.nestedPolicyIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'organizationIds': {
        return {
          label: t('organization-in-charge'),
          value:
            inputCascaderAndAccountingLabels.nestedOrganizationIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'otherResponsibleOrganizationIds': {
        return {
          label: t('other-organization-in-charge'),
          value:
            inputCascaderAndAccountingLabels.nestedOtherResponsibleOrganizationIds.join(
              ITEM_SPLITTER_COMMA,
            ),
        };
      }
      case 'formulaConditions': {
        const conditions = getNonNullableValue<typeof key>(_value);
        if (!conditions.length) {
          return [];
        }

        const conditionLabels = conditions.flatMap((condition) => {
          const label = getAmountSearchTagLabel({
            formulaCondition: condition,
            fromHistory: true,
          });

          return label ? [label] : [];
        });
        return {
          label: t('formula-conditions'),
          value: conditionLabels.join(', '),
        };
      }
      case 'lastImplementedFiscalYearRangeMin': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('last-implemented-fiscal-year-range-min'),
          value,
        };
      }
      case 'lastImplementedFiscalYearRangeMax': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('last-implemented-fiscal-year-range-max'),
          value,
        };
      }
      case 'indicatorKeyword': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('search-keyword-of-activity-indicator'),
          value,
        };
      }
      case 'indicatorKeywordSearchOnlyTitleAndDescription': {
        return {
          label: t('search-activity-indicator-name'),
          value: t('enabled'),
        };
      }
      case 'indicatorType': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('search-activity-indicator-item'),
          value: IndicatorTypeModifier[value],
        };
      }
      case 'hasOnlyQualitativeIndicator': {
        return {
          label: t('search-has-only-qualitative-indicator'),
          value: t('enabled'),
        };
      }
      case 'indicatorTerm': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('search-activity-indicator-term'),
          value: IndicatorTermModifier[value],
        };
      }
      case 'indicatorDataNatureType': {
        const value = getNonNullableValue<typeof key>(_value);
        return {
          label: t('search-activity-indicator-type'),
          value: IndicatorDataNatureTypeModifier[value],
        };
      }
      default: {
        const check: never = key;
        logger.error('key is not found', check);
        return [];
      }
    }
  });
};
